import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  LayoutDashboard,
  Users,
  UserPlus,
  Building2,
  ClipboardCheck,
  CreditCard,
  FileText,
  Settings,
  ShieldCheck,
  Shuffle,
  MessageSquare,
  LogOut,
  Bed,
  HelpCircle,
  Home,
  BarChart3,
  ArrowRight,
  Moon,
  Sun,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminPanelRole, type AdminRole, type Permission, hasAnyPermission as checkAnyPerm } from '../../constants/rbac';
import { useTheme } from '../../hooks/useTheme';

interface Command {
  id: string;
  label: string;
  group: string;
  icon: React.ComponentType<{ className?: string }>;
  keywords: string[];
  action: () => void;
  allowedRoles?: readonly string[];
  requiredPermission?: Permission;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

function fuzzyMatch(query: string, text: string): boolean {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();

  // Direct substring match
  if (lower.includes(q)) return true;

  // Fuzzy: every character in query appears in order in text
  let qi = 0;
  for (let i = 0; i < lower.length && qi < q.length; i++) {
    if (lower[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

function matchScore(query: string, text: string): number {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();

  // Exact match
  if (lower === q) return 100;
  // Starts with
  if (lower.startsWith(q)) return 90;
  // Word starts with
  const words = lower.split(/\s+/);
  if (words.some((w) => w.startsWith(q))) return 80;
  // Contains
  if (lower.includes(q)) return 70;
  // Fuzzy
  return 50;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const isAdmin = user && isAdminPanelRole(user.role);
  const { resolvedTheme, toggle: toggleTheme, setTheme } = useTheme();

  const allCommands = useMemo<Command[]>(() => {
    const nav = (href: string) => () => { navigate(href); onClose(); };
    const doLogout = () => { logout(); onClose(); };

    const adminCommands: Command[] = [
      { id: 'dashboard', label: 'Dashboard', group: 'Navigation', icon: LayoutDashboard, keywords: ['home', 'overview', 'dashboard'], action: nav('/dashboard') },
      { id: 'facilities', label: 'Facilities & Balloting', group: 'Navigation', icon: Building2, keywords: ['hostel', 'rooms', 'facility', 'setup', 'balloting'], action: nav('/facilities'), requiredPermission: 'hostels:view' },
      { id: 'booking-reviews', label: 'Booking Reviews', group: 'Navigation', icon: ClipboardCheck, keywords: ['bookings', 'review', 'approve', 'reject'], action: nav('/admin/booking-reviews'), requiredPermission: 'bookings:view' },
      { id: 'allocations', label: 'Allocations', group: 'Navigation', icon: Shuffle, keywords: ['allocate', 'ballot', 'assign', 'rooms'], action: nav('/admin/allocations'), requiredPermission: 'allocations:view' },
      { id: 'payments', label: 'Payments', group: 'Navigation', icon: CreditCard, keywords: ['payment', 'revenue', 'money', 'finance', 'reconcile'], action: nav('/payments'), requiredPermission: 'payments:view' },
      { id: 'bank-statements', label: 'Bank Statements', group: 'Navigation', icon: FileText, keywords: ['bank', 'statement', 'upload', 'transactions'], action: nav('/bank-statements'), requiredPermission: 'bank-statements:view' },
      { id: 'students', label: 'Students', group: 'Navigation', icon: Users, keywords: ['student', 'list', 'people', 'residents'], action: nav('/students'), requiredPermission: 'students:view' },
      { id: 'student-import', label: 'Student Import', group: 'Navigation', icon: UserPlus, keywords: ['import', 'upload', 'csv', 'bulk', 'add students'], action: nav('/student-import'), requiredPermission: 'students:import' },
      { id: 'support-tickets', label: 'Support Inbox', group: 'Navigation', icon: MessageSquare, keywords: ['support', 'tickets', 'help', 'inbox', 'messages'], action: nav('/support-tickets'), requiredPermission: 'support:view' },
      { id: 'settings', label: 'Settings', group: 'Navigation', icon: Settings, keywords: ['settings', 'config', 'preferences', 'room types'], action: nav('/settings'), requiredPermission: 'settings:view' },
      { id: 'staff-access', label: 'Team Access', group: 'Navigation', icon: ShieldCheck, keywords: ['staff', 'admin', 'access', 'invite', 'permissions', 'roles'], action: nav('/admin/staff-access'), requiredPermission: 'staff:view' },
      { id: 'hostel-portfolio', label: 'Hostel Portfolio', group: 'Navigation', icon: Home, keywords: ['hostel', 'portfolio', 'properties', 'manage'], action: nav('/admin/hostels'), requiredPermission: 'hostels:view' },
      { id: 'reports', label: 'Reports & Analytics', group: 'Navigation', icon: BarChart3, keywords: ['reports', 'analytics', 'charts', 'metrics', 'revenue', 'occupancy'], action: nav('/reports'), requiredPermission: 'reports:view' },
      { id: 'occupancy-grid', label: 'Occupancy Grid', group: 'Navigation', icon: BarChart3, keywords: ['calendar', 'grid', 'occupancy', 'beds', 'rooms', 'availability', 'map'], action: nav('/occupancy'), requiredPermission: 'occupancy:view' },
      { id: 'toggle-theme', label: resolvedTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode', group: 'Actions', icon: resolvedTheme === 'dark' ? Sun : Moon, keywords: ['dark', 'light', 'theme', 'mode', 'toggle', 'appearance'], action: () => { toggleTheme(); onClose(); } },
      { id: 'system-theme', label: 'Use System Theme', group: 'Actions', icon: Settings, keywords: ['system', 'auto', 'theme', 'os', 'preference'], action: () => { setTheme('system'); onClose(); } },
      { id: 'logout', label: 'Sign Out', group: 'Actions', icon: LogOut, keywords: ['logout', 'sign out', 'exit'], action: doLogout },
    ];

    const studentCommands: Command[] = [
      { id: 'student-dashboard', label: 'Dashboard', group: 'Navigation', icon: LayoutDashboard, keywords: ['home', 'overview', 'dashboard'], action: nav('/student/dashboard') },
      { id: 'student-bookings', label: 'My Bookings', group: 'Navigation', icon: Bed, keywords: ['bookings', 'reservation', 'room'], action: nav('/student/bookings') },
      { id: 'student-support', label: 'Support', group: 'Navigation', icon: HelpCircle, keywords: ['support', 'help', 'ticket', 'issue'], action: nav('/student/support') },
      { id: 'student-profile', label: 'Profile', group: 'Navigation', icon: Users, keywords: ['profile', 'account', 'settings', 'personal'], action: nav('/student/profile') },
      { id: 'book-hostel', label: 'Book a Hostel', group: 'Actions', icon: ArrowRight, keywords: ['book', 'reserve', 'hostel', 'new booking'], action: nav('/booking/reserve') },
      { id: 'toggle-theme', label: resolvedTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode', group: 'Actions', icon: resolvedTheme === 'dark' ? Sun : Moon, keywords: ['dark', 'light', 'theme', 'mode', 'toggle', 'appearance'], action: () => { toggleTheme(); onClose(); } },
      { id: 'system-theme', label: 'Use System Theme', group: 'Actions', icon: Settings, keywords: ['system', 'auto', 'theme', 'os', 'preference'], action: () => { setTheme('system'); onClose(); } },
      { id: 'logout', label: 'Sign Out', group: 'Actions', icon: LogOut, keywords: ['logout', 'sign out', 'exit'], action: doLogout },
    ];

    return isAdmin ? adminCommands : studentCommands;
  }, [isAdmin, navigate, onClose, logout, resolvedTheme, toggleTheme, setTheme]);

  const filteredCommands = useMemo(() => {
    const trimmed = query.trim();
    let results = allCommands;

    // Filter by permission for admin commands
    if (isAdmin && user) {
      results = results.filter((cmd) => {
        if (!cmd.requiredPermission) return true;
        return checkAnyPerm(user.role, [cmd.requiredPermission]);
      });
    }

    if (!trimmed) return results;

    return results
      .filter((cmd) => {
        const searchText = `${cmd.label} ${cmd.keywords.join(' ')}`;
        return fuzzyMatch(trimmed, searchText);
      })
      .sort((a, b) => {
        const aScore = Math.max(matchScore(trimmed, a.label), ...a.keywords.map((k) => matchScore(trimmed, k)));
        const bScore = Math.max(matchScore(trimmed, b.label), ...b.keywords.map((k) => matchScore(trimmed, k)));
        return bScore - aScore;
      });
  }, [query, allCommands, isAdmin, user]);

  // Group filtered commands
  const grouped = useMemo(() => {
    const groups: { label: string; items: Command[] }[] = [];
    const seen = new Set<string>();
    for (const cmd of filteredCommands) {
      if (!seen.has(cmd.group)) {
        seen.add(cmd.group);
        groups.push({ label: cmd.group, items: [] });
      }
      groups.find((g) => g.label === cmd.group)!.items.push(cmd);
    }
    return groups;
  }, [filteredCommands]);

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Keep active index in bounds
  useEffect(() => {
    if (activeIndex >= filteredCommands.length) {
      setActiveIndex(Math.max(0, filteredCommands.length - 1));
    }
  }, [filteredCommands.length, activeIndex]);

  // Scroll active item into view
  useEffect(() => {
    const activeEl = listRef.current?.querySelector('[data-active="true"]');
    activeEl?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const runCommand = useCallback(
    (cmd: Command) => {
      cmd.action();
    },
    [],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((i) => (i + 1) % filteredCommands.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((i) => (i - 1 + filteredCommands.length) % filteredCommands.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCommands[activeIndex]) {
            runCommand(filteredCommands[activeIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [filteredCommands, activeIndex, runCommand, onClose],
  );

  if (!open) return null;

  let flatIndex = -1;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-foreground/40 backdrop-blur-sm animate-fade-fast"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Palette */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="relative w-full max-w-lg rounded-xl border border-border bg-background shadow-xl animate-scale-in overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            placeholder="Search pages, actions..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:inline-flex h-5 items-center gap-0.5 rounded border border-border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-72 overflow-y-auto p-2" role="listbox">
          {filteredCommands.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">
              No results for &ldquo;{query}&rdquo;
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.label} role="group" aria-label={group.label}>
                <div className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </div>
                {group.items.map((cmd) => {
                  flatIndex++;
                  const isActive = flatIndex === activeIndex;
                  const idx = flatIndex;
                  const Icon = cmd.icon;
                  return (
                    <button
                      key={cmd.id}
                      role="option"
                      aria-selected={isActive}
                      data-active={isActive}
                      onClick={() => runCommand(cmd)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                        isActive
                          ? 'bg-primary/10 text-foreground'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-primary' : ''}`} />
                      <span className="flex-1 text-left font-medium">{cmd.label}</span>
                      {isActive && (
                        <kbd className="hidden sm:inline-flex h-5 items-center rounded border border-border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">
                          &crarr;
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="inline-flex h-4 items-center rounded border border-border bg-muted px-1 text-[10px]">&uarr;</kbd>
              <kbd className="inline-flex h-4 items-center rounded border border-border bg-muted px-1 text-[10px]">&darr;</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="inline-flex h-4 items-center rounded border border-border bg-muted px-1 text-[10px]">&crarr;</kbd>
              select
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="inline-flex h-4 items-center rounded border border-border bg-muted px-1 text-[10px]">esc</kbd>
            close
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
